import { I18n } from 'aws-amplify';
import { getIn, useFormikContext } from 'formik';
import { FunctionComponent, useMemo } from 'react';

import { useIsAssemblyBrandEnabled } from 'src/hooks/useIsAssemblyBrandEnabled';
import { useAppSelector } from 'src/hooks/useStore';
import {
  BaseTextField,
  SharpOutlinedTextField,
} from 'src/legacy/components/TextField';
import { isIdentityProviderDomain } from 'src/utils/AuthUtils';
import { removeSpaceFromString } from 'src/utils/StringUtils';

import { getAutoCompleteLabel } from '../utils';

import { OnboardingFormError } from './OnboardingFormError';
import { OnboardingLoginFormValues } from './onboardingFormTypes';

const genericSecurityError = 'Invalid email or password. Please try again.';
interface OnboardingLoginFormFieldsProps {
  isAdminLogin: boolean;
  isPortalLogin: boolean;
}

export const OnboardingLoginFormFields: FunctionComponent<
  OnboardingLoginFormFieldsProps
> = ({ isAdminLogin, isPortalLogin }) => {
  const { errors, handleBlur, handleChange, isSubmitting, touched, values } =
    useFormikContext<OnboardingLoginFormValues>();

  const { isAssemblyBrandEnabled } = useIsAssemblyBrandEnabled();

  // Check if password field should be hidden (admin login or IDP domain)
  const isPasswordHidden =
    isAdminLogin || (isPortalLogin && isIdentityProviderDomain(values.email));

  const { errorMsg } = useAppSelector((state) => ({
    googleSignedIn: state.auth.signedIn,
    errorMsg: state.auth.error,
  }));

  // errorToSurface will check if we should mask error message for any error message we don't want to surface to the user for security reasons.
  // If this is true, we'll use the generic security error message.
  const errorToSurface = (eMsg: string) => {
    if (
      eMsg == 'User does not exist.' ||
      eMsg == 'Incorrect username or password.'
    ) {
      return genericSecurityError;
    }
    return eMsg;
  };

  /**
   * This function is used to get the error text to display
   * @param errorsState formik errors state
   * @returns
   */
  const formErrorText = useMemo(() => {
    const passwordTouched: boolean = getIn(touched, 'password');
    const emailTouched: boolean = getIn(touched, 'email');
    const passwordError: string | undefined = getIn(errors, 'password');
    const emailError: string | undefined = getIn(errors, 'email');
    const authError: string | undefined = errorMsg;

    if (passwordError && emailError && passwordTouched && emailTouched) {
      return genericSecurityError; // render generic security error when both password and email are invalid
    }

    let err = '';
    if (passwordError && passwordTouched) {
      err = passwordError;
    }
    if (emailError && emailTouched) {
      err = emailError;
    }
    if (authError) {
      err = authError;
    }

    return err ? errorToSurface(err) : null;
  }, [errors, touched, errorMsg]);

  const getCommonProps = (
    fieldName: keyof OnboardingLoginFormValues,
    isAssembly: boolean,
  ) => ({
    autoComplete: getAutoCompleteLabel(fieldName),
    disabled: isSubmitting,
    error: Boolean(touched[fieldName] && errors[fieldName]),
    fullWidth: true,
    inputProps: { 'data-testid': `${fieldName}Input` },
    key: fieldName,
    name: fieldName,
    type: fieldName,
    onBlur: handleBlur,
    onInput: handleChange,
    variant: 'outlined' as const,
    value:
      fieldName === 'email'
        ? values.email
          ? removeSpaceFromString(values.email)
          : ''
        : values.password,
    ...(isAssembly
      ? {
          InputLabelProps: { shrink: true },
          InputProps: { className: 'mt-2' },
          sizeVariant: 'medium' as const,
        }
      : {
          sizeVariant: 'tall' as const,
        }),
  });

  if (isAssemblyBrandEnabled) {
    return (
      <>
        <BaseTextField
          {...getCommonProps('email', true)}
          autoFocus
          className="mb-4 [&_.MuiOutlinedInput-root:not(.Mui-error):focus-within_fieldset]:border-black-heading!"
          id="login-username"
          label="Email"
          placeholder="name@company.com"
        />

        {isPasswordHidden ? null : (
          <>
            <BaseTextField
              {...getCommonProps('password', true)}
              autoFocus={Boolean(values.email)}
              className="mb-6 [&_.MuiOutlinedInput-root:not(.Mui-error):focus-within_fieldset]:border-black-heading!"
              id="login-password"
              label={
                isAdminLogin || isPortalLogin ? 'Password' : 'Set password'
              }
              type="password"
            />

            <OnboardingFormError errorText={formErrorText ?? ''} />
          </>
        )}
      </>
    );
  }

  return (
    <>
      <SharpOutlinedTextField
        {...getCommonProps('email', false)}
        autoFocus
        id="login-username"
        label={I18n.get('Work email')}
        placeholder={I18n.get('Work email')}
      />

      {isPasswordHidden ? null : (
        <>
          <SharpOutlinedTextField
            {...getCommonProps('password', false)}
            autoFocus={Boolean(values.email)}
            id="login-password"
            label={I18n.get('Password')}
            placeholder={I18n.get('Password')}
          />

          <OnboardingFormError errorText={formErrorText ?? ''} />
        </>
      )}
    </>
  );
};
